"use client";

import { LocaleLink } from "@/components/shared/LocaleLink";
import { GTMEvents, GTMSubEvents, PushDataToGTM } from "@/lib/gtm";

export type MegaIconId =
  | "wrench"
  | "package"
  | "tag"
  | "mail"
  | "mapPin"
  | "calendar"
  | "sparkles"
  | "car"
  | "bolt"
  | "plug";

export type MegaSubItem = {
  href: string;
  icon: MegaIconId;
  titleKey: string;
  descKey: string;
  label: string;
};

export const megaMenuItemsByKey: Record<string, MegaSubItem[]> = {
  services: [
    { href: "/services", icon: "wrench", titleKey: "nav.mega.services.allTitle", descKey: "nav.mega.services.allDesc", label: "All car services" },
    // { href: "/products", icon: "package", titleKey: "nav.mega.products.careTitle", descKey: "nav.mega.products.careDesc", label: "Car care products" },
    { href: "/packages", icon: "package", titleKey: "nav.mega.packages.allTitle", descKey: "nav.mega.packages.allDesc", label: "All packages" },
    // { href: "/services", icon: "calendar", titleKey: "nav.mega.services.periodicTitle", descKey: "nav.mega.services.periodicDesc", label: "Periodic maintenance" },
    { href: "/find-workshop", icon: "mapPin", titleKey: "nav.mega.services.workshopTitle", descKey: "nav.mega.services.workshopDesc", label: "Find a workshop" },
    { href: "/my-garage", icon: "car", titleKey: "nav.mega.services.garageTitle", descKey: "nav.mega.services.garageDesc", label: "My garage" },
  ],
  offers: [{ href: "/offers", icon: "tag", titleKey: "nav.mega.offers.currentTitle", descKey: "nav.mega.offers.currentDesc", label: "Current promotions" }],
  about: [
    { href: "/about", icon: "mapPin", titleKey: "nav.mega.about.globalTitle", descKey: "nav.mega.about.globalDesc", label: "About us" },
    { href: "/ev-services", icon: "plug", titleKey: "nav.mega.about.evServicesTitle", descKey: "nav.mega.about.evServicesDesc", label: "EV services" },
    { href: "/petromin-express", icon: "sparkles", titleKey: "nav.mega.about.expressTitle", descKey: "nav.mega.about.expressDesc", label: "Petromin Express" },
    { href: "/petromin-autocare", icon: "wrench", titleKey: "nav.mega.about.careTitle", descKey: "nav.mega.about.careDesc", label: "Petromin Autocare" },
    { href: "/body-paint", icon: "package", titleKey: "nav.mega.about.bodyPaintTitle", descKey: "nav.mega.about.bodyPaintDesc", label: "Petromin Body & paint" },
    { href: "/petromin-commercial", icon: "car", titleKey: "nav.mega.about.commercialTitle", descKey: "nav.mega.about.commercialDesc", label: "Petromin commercial vehicle care" },
    { href: "/luxury-repair", icon: "bolt", titleKey: "nav.mega.about.luxuryRepairTitle", descKey: "nav.mega.about.luxuryRepairDesc", label: "Luxury vehicle repair" },
    { href: "/national-auto-parts", icon: "car", titleKey: "nav.mega.about.autoPartsTitle", descKey: "nav.mega.about.autoPartsDesc", label: "National auto parts" },
    { href: "/national-oil-stop", icon: "sparkles", titleKey: "nav.mega.about.oilStopTitle", descKey: "nav.mega.about.oilStopDesc", label: "National oil stop" },
    { href: "/petromin-tristar", icon: "car", titleKey: "nav.mega.about.tristarTitle", descKey: "nav.mega.about.tristarDesc", label: "Petromin Tristar" },
  ],
  contact: [
    { href: "/contact", icon: "mail", titleKey: "nav.mega.contact.reachTitle", descKey: "nav.mega.contact.reachDesc", label: "Contact channels" },
    { href: "/find-workshop", icon: "mapPin", titleKey: "nav.mega.contact.locationsTitle", descKey: "nav.mega.contact.locationsDesc", label: "Workshop map" },
  ],
};

export function MegaIcon({ id, className }: { id: MegaIconId; className?: string }) {
  const common = { className, fill: "none" as const, stroke: "currentColor", strokeWidth: 1.75, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
  switch (id) {
    case "wrench":
      return (
        <svg viewBox="0 0 24 24" aria-hidden {...common}>
          <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.8-3.8a6 6 0 0 1-8.4 8.4l-6.9 6.9a2 2 0 1 1-2.8-2.8l6.9-6.9a6 6 0 0 1 8.4-8.4z" />
        </svg>
      );
    case "package":
      return (
        <svg viewBox="0 0 24 24" aria-hidden {...common}>
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
          <path d="M3.27 6.96 12 12.01l8.73-5.05M12 22.08V12" />
        </svg>
      );
    case "tag":
      return (
        <svg viewBox="0 0 24 24" aria-hidden {...common}>
          <path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z" />
          <circle cx="7.5" cy="7.5" r=".5" fill="currentColor" stroke="none" />
        </svg>
      );
    case "mail":
      return (
        <svg viewBox="0 0 24 24" aria-hidden {...common}>
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
          <path d="m22 6-10 7L2 6" />
        </svg>
      );
    case "mapPin":
      return (
        <svg viewBox="0 0 24 24" aria-hidden {...common}>
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
          <circle cx="12" cy="10" r="3" />
        </svg>
      );
    case "calendar":
      return (
        <svg viewBox="0 0 24 24" aria-hidden {...common}>
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <path d="M16 2v4M8 2v4M3 10h18" />
        </svg>
      );
    case "sparkles":
      return (
        <svg viewBox="0 0 24 24" aria-hidden {...common}>
          <path d="m12 3 1.9 5.8h6.1l-5 3.6 1.9 5.8-5-3.6-5 3.6 1.9-5.8-5-3.6h6.1z" />
        </svg>
      );
    case "car":
      return (
        <svg viewBox="0 0 24 24" aria-hidden {...common}>
          <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9L18 8h-4l-1.3-2.6a2 2 0 0 0-1.8-1.1H8.5a2 2 0 0 0-1.8 1.1L5 8H1v9h2" />
          <circle cx="7" cy="17" r="2" />
          <path d="M9 17h6" />
          <circle cx="17" cy="17" r="2" />
        </svg>
      );
    case "bolt":
      return (
        <svg viewBox="0 0 24 24" aria-hidden {...common}>
          <path d="M13 2 3 14h8l-1 8 10-12h-8l1-8z" />
        </svg>
      );
    case "plug":
      return (
        <svg viewBox="0 0 24 24" aria-hidden {...common}>
          <path d="M7 2v4M17 2v4" />
          <path d="M5 6h14v5a7 7 0 0 1-14 0V6z" />
          <path d="M10 18v4M14 18v4" />
        </svg>
      );
    default:
      return null;
  }
}

export function MegaMenuDropdown({
  items,
  open,
  isRtl,
  navRowPaddingBottomPx = 0,
  t,
  menuLabel,
  onMouseEnter,
  onMouseLeave,
}: {
  items: MegaSubItem[];
  open: boolean;
  isRtl: boolean;
  /** When desktop nav row has padding-bottom, panel top aligns flush below it (matches Header inset). */
  navRowPaddingBottomPx?: number;
  t: (path: string) => string;
  menuLabel: string;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}) {
  if (!open || items.length === 0) return null;

  const count = items.length;
  const useTwoColumns = count > 3;

  return (
    <div
      className={`absolute z-[100] ${isRtl ? "right-0" : "left-0"}`}
      style={{
        top: `calc(100% + ${navRowPaddingBottomPx}px)`,
      }}
      role="region"
      aria-label={menuLabel}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {/* Panel starts at column bottom (top-full); shadow biased downward to avoid creeping over labels */}
      <div
        className={`rounded-t-none rounded-b-xl border-x border-b border-[#E5E5E5] bg-white p-2 shadow-[0_14px_28px_rgba(19,15,14,0.08)] ${
          useTwoColumns
            ? "w-[min(calc(100vw-2rem),42rem)]"
            : "w-[min(calc(100vw-2rem),22rem)]"
        }`}
      >
        <ul
          className={`grid gap-0.5 ${useTwoColumns ? "grid-cols-2" : "grid-cols-1"}`}
        >
          {items.map((item) => (
            <li key={`${item.href}-${item.titleKey}`} className="min-w-0">
              <LocaleLink
                href={item.href}
                onClick={() => {
                  PushDataToGTM(GTMEvents.DataLayerUpdate, {
                    pageName: "DESKTOP HEADER",
                    pageCategory: "DESKTOP HEADER",
                    eventCategory: GTMSubEvents.Click,
                    eventLabel: `DESKTOP HEADER > ${item.label.toUpperCase()}`,
                  });
                }}
                className={`flex h-full gap-3 rounded-lg px-3 py-3 transition-colors hover:bg-[#F4F4F4] focus-visible:bg-[#F4F4F4] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#DD0428]/30 ${isRtl ? "flex-row-reverse" : ""}`}
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#BD2226]/10 text-[#BD2226]">
                  <MegaIcon id={item.icon} className="h-5 w-5" />
                </span>
                <span className="min-w-0 flex-1 text-start">
                  <span className="block text-[14px] font-semibold text-[#3A3A3C]">{t(item.titleKey)}</span>
                  <span className="mt-0.5 block text-[12px] leading-snug text-[#6B7280]">{t(item.descKey)}</span>
                </span>
              </LocaleLink>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
